import React, { useState } from 'react';
import { Users, Copy, Check, Crown, Trash2, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import ShareInvite from '../../components/ShareInvite';
import { getGameStatus, getStatusConfig, isGameCompleted, getWinnerDisplay, FINISHED_STATUSES, rolloverLabel } from './gameStatus';

/**
 * A league names its games after itself, so under a league heading the row read
 * "Moes Tavern - Championship 2026-27" directly below a heading saying "Moes Tavern".
 * Strip the part the heading already said. Anything left over is what actually
 * distinguishes this game from the others in the league - the competition, the cycle.
 *
 * Falls back to the whole name rather than to an empty one: a league whose games are
 * named after it exactly is a game with nothing left to call itself.
 */
export const shortNameFor = (name, leagueName) => {
  if (!name || !leagueName) return name;
  if (!name.toLowerCase().startsWith(leagueName.toLowerCase())) return name;
  const rest = name.slice(leagueName.length).replace(/^[\s\-–—:]+/, '').trim();
  return rest || name;
};

/**
 * One game in the list.
 *
 * Deliberately thin. A row in a list has one job - be recognised without being read -
 * so it carries what is required of you, what it is called, and how many are left.
 * Everything else lives on the game screen.
 *
 * The exception is the roster, which is behind the player count for a manager of an
 * open game. It is a destructive action on a browsing surface, so it is one deliberate
 * tap away rather than sitting open; but it only exists here, so hiding it outright
 * would take the capability away rather than move it.
 */
function GameRow({ game, leagueName, currentUser, copiedCode, onCopyCode, onRemovePlayer, onViewGame }) {
  const [showRoster, setShowRoster] = useState(false);

  const status = getStatusConfig(game);
  const gameStatus = getGameStatus(game);
  const completed = isGameCompleted(game);
  const winner = getWinnerDisplay(game);
  const cancelled = FINISHED_STATUSES.includes(gameStatus) && !completed;

  const isManager = game.manager && game.manager.id === currentUser.id;
  const open = gameStatus === 'OPEN';
  const players = game.players || [];
  const remaining = game.remainingPlayers?.length;

  // The window after a rollover is the server's answer (rolloverWindowOpen), never worked out
  // here: the manager may take people out then too (backlog item 49).
  const rosterOpen = open || Boolean(game.rolloverWindowOpen);
  const canRemove = (player) => isManager && player.id !== currentUser.id
    && (gameStatus === 'OPEN' || gameStatus === 'STARTED' || Boolean(game.rolloverWindowOpen));

  const headcount = open
    ? `${players.length} joined`
    : (typeof remaining === 'number' ? `${remaining} of ${players.length} still standing` : `${players.length} players`);

  const tournament = game.tournament?.name || game.tournament?.tournamentName;

  return (
    <div className={`game-row${completed ? ' is-complete' : ''}${cancelled ? ' is-cancelled' : ''}`}>
      <div className="game-row-main">
        <button className="game-row-open" onClick={() => onViewGame(game.id)}>
          <span className="game-row-name">
            {shortNameFor(game.name, leagueName)}
            {isManager && <Crown size={14} className="game-row-crown" aria-label="You manage this game" />}
          </span>
          {/* Inside a league the competition is already in the name the heading left behind,
              so the row says only which week it reached. On its own it has to say both. */}
          <span className="game-row-meta">
            {leagueName ? `Week ${game.gameWeek}` : (
              <>{tournament || 'Tournament'}{!open && <> &middot; Week {game.gameWeek}</>}</>
            )}
          </span>
        </button>
        <span
          className="status-badge"
          style={{ backgroundColor: status.color, color: status.textColor }}
        >
          {status.label}
        </span>
      </div>

      {game.rolloverCount > 0 && (
        <div className="game-row-rollover">
          <RotateCcw size={14} aria-hidden="true" /> {rolloverLabel(game)}
          {isManager && game.rolloverWindowOpen ? ' · you can let people in or out now' : null}
        </div>
      )}

      {/* Nobody is still standing in a game that was called off - the roster it stopped
          with is not a survivor count, and printing one reads as a game still going. */}
      {completed && winner ? (
        <div className="game-row-winner">{winner}</div>
      ) : cancelled ? null : (
        <div className="game-row-count">
          {isManager && rosterOpen ? (
            <button
              className="roster-toggle"
              onClick={() => setShowRoster((s) => !s)}
              aria-expanded={showRoster}
            >
              <Users size={14} />
              {headcount}
              {showRoster ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          ) : (
            <><Users size={14} /> {headcount}</>
          )}
        </div>
      )}

      {showRoster && (
        <div className="game-row-roster">
          {players.map((player) => (
            <div key={player.id} className="player-item">
              <span className="player-name">
                {player.displayName}
                {player.id === currentUser.id && ' (You)'}
              </span>
              {canRemove(player) && (
                <button
                  className="remove-player-btn"
                  onClick={() => onRemovePlayer(game.id, player.id, player.displayName)}
                  title="Remove player"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Only while somebody can use it: an open game, or - for its manager - the window after a
          rollover, when the code raises a request rather than a seat. Otherwise it is furniture. */}
      {Boolean((open || (isManager && game.rolloverWindowOpen)) && game.gameCode) && (
        <div className="game-row-code">
          <span className="code-value">{game.gameCode}</span>
          <button className="copy-btn" onClick={() => onCopyCode(game.gameCode)} title="Copy game code">
            {copiedCode === game.gameCode ? <Check size={16} /> : <Copy size={16} />}
          </button>
          <ShareInvite kind="game" name={game.name} code={game.gameCode} straightIn={open} />
        </div>
      )}
    </div>
  );
}

export default GameRow;
